import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import m_logo from '../logo.png';

// Font Awesome CDN for icons
const fontAwesomeCDN = document.createElement("link");
fontAwesomeCDN.rel = "stylesheet";
fontAwesomeCDN.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css";
document.head.appendChild(fontAwesomeCDN);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

// Password validation function
const validatePassword = (password) => {
  if (!password) return "Password is required";
  if (password.length < 8) return "Minimum 8 characters required";
  if (!/[A-Z]/.test(password)) return "At least one uppercase letter required";
  if (!/[0-9]/.test(password)) return "At least one number required";
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) 
    return "At least one special character required";
  return "";
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #ffffff 0%, #a494ee70 100%)",
    padding: "15px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  container: {
    display: "flex",
    width: "100%",
    maxWidth: "1100px",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  imageSection: {
    flex: "1.2",
    background: "linear-gradient(145deg, #0183f5, #060606)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "30px",
  },
  imageContent: {
    textAlign: "center",
    color: "white",
    width: "100%",
  },
  welcomeBadge: {
    background: "rgba(255, 255, 255, 0.15)",
    padding: "6px 20px",
    borderRadius: "50px",
    display: "inline-block",
    marginBottom: "20px",
    fontSize: "13px",
    fontWeight: "600",
    letterSpacing: "1px",
  },
  welcomeTitle: {
    fontSize: "28px",
    fontWeight: "800",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  welcomeSubtitle: {
    fontSize: "13px",
    marginBottom: "25px",
    opacity: "0.95",
    lineHeight: "1.5",
  },
  logoContainer: {
    marginBottom: "25px",
  },
  govtBadge: {
    background: "rgba(255, 255, 255, 0.1)",
    padding: "15px",
    borderRadius: "12px",
  },
  govtTitle: {
    fontSize: "20px",
    marginBottom: "4px",
    fontWeight: "500",
  },
  govtSubtitle: {
    fontSize: "20px",
    fontWeight: "800",
  },
  formSection: {
    flex: "1",
    background: "white",
    padding: "25px 30px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflowY: "auto",
  },
  formHeader: {
    marginBottom: "15px",
  },
  formTitle: {
    fontSize: "26px",
    marginBottom: "4px",
    marginTop: 0,
    fontWeight: "bold",
    color: "#0d47a1",
  },
  formSubtitle: {
    color: "#546e7a",
    fontSize: "13px",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  label: {
    color: "#37474f",
    fontWeight: "600",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  labelIcon: {
    color: "#0d47a1",
    fontSize: "13px",
    width: "14px",
  },
  required: {
    color: "#d32f2f",
    fontSize: "13px",
    marginLeft: "3px",
  },
  inputContainer: {
    position: "relative",
    width: "100%",
  },
  input: (hasError, hasIcon = false) => ({
    width: "100%",
    padding: `10px ${hasIcon ? '38px' : '12px'} 10px 12px`,
    borderRadius: "6px",
    border: `1px solid ${hasError ? "#d32f2f" : "#b0bec5"}`,
    fontSize: "14px",
    boxSizing: "border-box",
    background: "#fff",
    outline: "none",
    "&:focus": {
      borderColor: "#0d47a1",
      boxShadow: "0 0 0 2px rgba(13, 71, 161, 0.1)",
    },
  }),
  passwordToggle: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#0d47a1",
    fontSize: "15px",
    padding: "4px",
  },
  help: {
    color: "#d32f2f",
    fontSize: "11px",
    marginTop: "2px",
  },
  passwordHint: {
    color: "#6c757d",
    fontSize: "10px",
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  registerButton: {
    padding: "12px",
    background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 10px rgba(46, 125, 50, 0.3)",
    },
    "&:disabled": {
      background: "#b0bec5",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  alert: (type) => ({
    padding: "8px 12px",
    borderRadius: "6px",
    marginBottom: "12px",
    fontWeight: "500",
    background: type === "success" ? "#e8f5e9" : type === "error" ? "#ffebee" : "#e3f2fd",
    color: type === "success" ? "#1b5e20" : type === "error" ? "#c62828" : "#0d47a1",
    border: `1px solid ${type === "success" ? "#c8e6c9" : type === "error" ? "#ffcdd2" : "#bbdefb"}`,
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    marginTop: "15px",
    paddingTop: "12px",
    borderTop: "1px solid #e0e0e0",
  },
  footerLink: {
    color: "#0d47a1",
    fontSize: "13px",
    textDecoration: "none",
    cursor: "pointer",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  passwordStrength: {
    marginTop: "4px",
    height: "3px",
    background: "#e0e0e0",
    borderRadius: "2px",
    overflow: "hidden",
  },
  strengthBar: (strength) => ({
    height: "100%",
    width: strength === "weak" ? "33%" : strength === "medium" ? "66%" : "100%",
    background: strength === "weak" ? "#dc3545" : strength === "medium" ? "#ffc107" : "#28a745",
  }),
  strengthText: (strength) => ({
    fontSize: "10px",
    fontWeight: "600",
    color: strength === "weak" ? "#dc3545" : strength === "medium" ? "#856404" : "#28a745",
    marginTop: "2px",
  }),
  successCard: {
    background: "#e8f5e9",
    border: "2px solid #c8e6c9",
    borderRadius: "10px",
    padding: "25px",
    textAlign: "center",
  },
  successIcon: {
    fontSize: "48px",
    marginBottom: "12px",
    color: "#2e7d32",
  },
  successTitle: {
    fontSize: "22px",
    fontWeight: "bold",
    color: "#2e7d32",
    marginBottom: "10px",
  },
  successMessage: {
    fontSize: "13px",
    color: "#1b5e20",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  checkboxLabel: {
    color: "#37474f",
    fontSize: "12px",
    fontWeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
  },
};

// Add CSS animations
const styleSheet = document.createElement("style");
styleSheet.innerText = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;
document.head.appendChild(styleSheet);

const VisitorPage = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const checkPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 8) return 'weak';
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const strength = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;
    if (strength <= 2) return 'weak';
    if (strength === 3) return 'medium';
    return 'strong';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    const passError = validatePassword(formData.password);
    if (passError) {
      newErrors.password = passError;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setMessage({ 
        text: 'Please fill all required fields correctly', 
        type: 'error' 
      });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const visitorData = {
        username: formData.username.trim(),
        full_name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.replace(/[^0-9]/g, ''),
        password: formData.password
      };

      console.log('Submitting visitor registration:', visitorData);

      const response = await axios.post(`${API_BASE}/visitor/register`, visitorData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000
      });

      if (response.data.success) {
        setSubmitted(true);
        setMessage({
          text: 'Visitor registration successful! You can now login.',
          type: 'success'
        });
        
        sessionStorage.setItem('visitor_registration', JSON.stringify({
          username: formData.username,
          email: formData.email,
          registeredAt: new Date().toISOString()
        }));
      } else {
        setMessage({
          text: `❌ ${response.data.error || 'Registration failed'}`,
          type: 'error'
        });
      }

    } catch (error) {
      console.error('Visitor registration error:', error);
      
      let errorMessage = 'Failed to register. Please try again.';
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = error.response.data?.error || 'Invalid data provided';
        } else if (error.response.status === 409) {
          errorMessage = 'Username or email already exists';
        } else {
          errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      }
      
      setMessage({
        text: `❌ ${errorMessage}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false
    });
    setErrors({});
    setSubmitted(false);
    setMessage({ text: '', type: '' });
    setPasswordStrength('');
  };

  const goToLogin = () => {
    navigate('/login');
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.imageSection}>
            <div style={styles.imageContent}>
              <div style={styles.welcomeBadge}>
                <i className="fas fa-check-circle" style={{ marginRight: "6px" }}></i>
                REGISTRATION SUCCESS
              </div>
              <div style={styles.welcomeTitle}>Welcome to DMA</div>
              <div style={styles.logoContainer}>
                <img src={m_logo} alt="Logo" style={{ width: "100px" }} />
              </div>
            </div>
          </div>
          <div style={styles.formSection}>
            <div style={styles.successCard}>
              <div style={styles.successIcon}><i className="fas fa-check-circle"></i></div>
              <h2 style={styles.successTitle}>Registration Successful!</h2>
              <p style={styles.successMessage}>
                Your visitor account has been created successfully.<br />
                Username: <strong>{formData.username}</strong><br />
                You can now login with your credentials.
              </p>
              <button onClick={goToLogin} style={styles.registerButton}>
                <i className="fas fa-sign-in-alt"></i> Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Left Side */}
        <div style={styles.imageSection}>
          <div style={styles.imageContent}>
            <div style={styles.welcomeBadge}>
              <i className="fas fa-user-plus" style={{ marginRight: "6px" }}></i>
              VISITOR REGISTRATION
            </div>
            <div style={styles.welcomeTitle}>Register as Visitor</div>
            <div style={styles.welcomeSubtitle}>
              Create a visitor account to access public information and documents
            </div>
            <div style={styles.logoContainer}>
              <img src={m_logo} alt="Logo" style={{ width: "100px" }} />
            </div>
            <div style={styles.govtBadge}>
              <div style={styles.govtTitle}>Government Of Maharashtra</div>
              <div style={styles.govtSubtitle}>Directorate of Municipal Administration</div>
            </div>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div style={styles.formSection}>
          <div style={styles.formHeader}>
            <h1 style={styles.formTitle}>Visitor Registration</h1>
            <p style={styles.formSubtitle}>Fill in your details to create a visitor account</p>
          </div>

          {message.text && (
            <div style={styles.alert(message.type)}>
              {message.type === 'success' ? <i className="fas fa-check-circle"></i> : <i className="fas fa-exclamation-circle"></i>}
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Basic Information - 2 column grid */}
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>
                  <i className="fas fa-user" style={styles.labelIcon}></i>
                  Username <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="Choose a username"
                  style={styles.input(!!errors.username)}
                />
                {errors.username && <span style={styles.help}>{errors.username}</span>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  <i className="fas fa-signature" style={styles.labelIcon}></i>
                  Full Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  style={styles.input(!!errors.name)}
                />
                {errors.name && <span style={styles.help}>{errors.name}</span>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  <i className="fas fa-envelope" style={styles.labelIcon}></i>
                  Email Address <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter your email"
                  style={styles.input(!!errors.email)}
                />
                {errors.email && <span style={styles.help}>{errors.email}</span>}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  <i className="fas fa-phone" style={styles.labelIcon}></i>
                  Phone Number <span style={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  style={styles.input(!!errors.phone)}
                />
                {errors.phone && <span style={styles.help}>{errors.phone}</span>}
              </div>
            </div>

            {/* Password fields */}
            <div style={styles.field}>
              <label style={styles.label}>
                <i className="fas fa-lock" style={styles.labelIcon}></i>
                Password <span style={styles.required}>*</span>
                <div style={styles.passwordHint}>
                <i className="fas fa-info-circle"></i>
                <span>Min 8 chars, 1 uppercase, 1 number, 1 special character</span>
              </div>
              </label>
              
              <div style={styles.inputContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Create a strong password"
                  style={styles.input(!!errors.password, true)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                </button>
              </div>
              {formData.password && (
                <>
                  <div style={styles.passwordStrength}>
                    <div style={styles.strengthBar(passwordStrength)} />
                  </div>
                  
                </>
              )}
              
              {errors.password && <span style={styles.help}>{errors.password}</span>}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>
                <i className="fas fa-check-circle" style={styles.labelIcon}></i>
                Confirm Password <span style={styles.required}>*</span>
              </label>
              <div style={styles.inputContainer}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="Confirm your password"
                  style={styles.input(!!errors.confirmPassword, true)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.passwordToggle}
                >
                  {showConfirmPassword ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                </button>
              </div>
              {errors.confirmPassword && <span style={styles.help}>{errors.confirmPassword}</span>}
            </div>

            {/* Terms and Conditions */}
            <div style={styles.field}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleChange('agreeToTerms', e.target.checked)}
                  style={{ marginRight: "8px", transform: "scale(1.1)" }}
                />
                I agree to the <Link to="/terms" style={{ color: "#0d47a1" }}>Terms and Conditions</Link> and 
                <Link to="/privacy" style={{ color: "#0d47a1", marginLeft: "4px" }}>Privacy Policy</Link>
                <span style={styles.required}>*</span>
              </label>
              {errors.agreeToTerms && <span style={styles.help}>{errors.agreeToTerms}</span>}
            </div>

            {/* Register Button */}
            <button type="submit" disabled={loading} style={styles.registerButton}>
              {loading ? <><i className="fas fa-spinner fa-spin"></i> Creating Account...</> : <><i className="fas fa-user-plus"></i> Register as Visitor</>}
            </button>
          </form>

          {/* Footer Links */}
          <div style={styles.footerLinks}>
            <Link to="/login" style={styles.footerLink}>
              <i className="fas fa-arrow-left"></i> Already have an account? Login here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorPage;